import { makeStyles, Typography } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import type { RenderableTotals } from '@paintscout/util/builder'
import { formatCurrency, getDetails, getObjectLabels, getQuoteOptions, getTotals } from '@paintscout/util/builder'
import React from 'react'
import { useQuote } from '../context'
import OutlinedTableCard from './OutlinedTableCard'
import PackageSelectedRow from '../Quote/views/QuoteView/TieredPackages/PackageSelectedRow'
import classNames from 'classnames'
import type { Theme } from '@paintscout/ui/styles'
import { useWisetackSection, WisetackApplicationNext, WisetackPromo } from '../Wisetack'

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {
        display: 'flex',
        justifyContent: 'flex-end',
        // On phones the summary is centered instead of right-aligned (mobile design).
        [theme.breakpoints.down('xs')]: {
          justifyContent: 'center'
        }
      },
      card: {
        width: '100%',
        maxWidth: 360,
        // Phones run the summary the full width of the item shells above it (ENG-2075, prototype).
        [theme.breakpoints.down('xs')]: {
          maxWidth: 'none'
        }
      },
      // When the total is clickable (editor), the whole box opens the Calculations drawer.
      clickable: {
        cursor: 'pointer',
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
          borderRadius: theme.shape.borderRadius
        }
      },
      breakdown: {
        padding: theme.spacing(0.5)
      },
      // With no discount, tax, subtotal, package, payment or Wisetack block, the grand-total row is
      // the card's only content — and its own border then sits 4px inside the card's, which reads as
      // a doubled outline (ENG-1917). Drop the padding so the row *is* the card: it keeps one border
      // (the card's) and one fill, clipped to the card's radius by `OutlinedTableCard`'s overflow.
      breakdownSolo: {
        padding: 0
      },
      breakdownRows: {
        padding: theme.spacing(0.75, 0)
      },
      // 14px throughout, with the grand total the one figure that steps up — the summary reads as
      // a block of small type, not as body copy.
      row: {
        padding: theme.spacing(1.2, 1),
        paddingLeft: theme.spacing(1.5),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        color: neutral.P60,
        fontSize: theme.typography.pxToRem(14),
        '& span': {
          fontSize: 'inherit'
        }
      },
      headerRow: {
        border: `1px solid ${neutral.P85}`,
        borderRadius: theme.shape.borderRadius,
        '& span': {
          fontWeight: 500,
          color: neutral.P40
        }
      },
      totalRow: {
        backgroundColor: neutral.P96,
        '& > span:last-child': {
          fontSize: theme.typography.pxToRem(16)
        }
      },
      // The solo grand-total row: no border of its own, and no radius — the card around it draws
      // both. See {@link breakdownSolo}.
      totalRowSolo: {
        border: 'none',
        borderRadius: 0
      },
      noBottomBorder: {
        borderBottom: 'none',
        borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`
      },
      paymentSection: {
        padding: theme.spacing(1, 1.5),
        border: `1px solid ${neutral.P85}`,
        borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`
      },
      // When the Wisetack block follows, it draws the bottom border/corners instead — otherwise
      // the two boxes each round their touching edge and the seam between them looks broken.
      paymentSectionJoined: {
        borderBottom: 'none',
        borderRadius: 0
      },
      paymentRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(0.25, 0),
        fontSize: theme.typography.pxToRem(14),
        '& span': {
          fontSize: 'inherit'
        },
        '& > span:first-child': {
          fontWeight: 500
        }
      },
      // No padding: PackageSelectedRow pads itself, so its hover fill covers the whole block.
      packageBlock: {
        border: `1px solid ${neutral.P85}`,
        borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
        overflow: 'hidden'
      },
      wisetackPromoWrapper: {
        border: `1px solid ${neutral.P85}`,
        borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
        paddingTop: theme.spacing(1.5),
        background: neutral.P98
      }
    }
  },
  { name: 'TotalTableNext' }
)

interface BreakdownRow {
  key: string
  label: string
  value: number
  prefixPlus?: boolean
}

/**
 * Stand-in for a document whose value is zero, where `getTotals` hands back `{}`. Only the fields this
 * table reads are set; every branch below then renders the same way it would for a real $0.00 total.
 */
const ZERO_TOTALS = {
  subTotal: 0,
  customSubTotal: false,
  discounts: [],
  taxes: [],
  afterDiscount: 0,
  afterTax: 0,
  amountPaid: 0,
  balanceDue: 0,
  additionalWork: 0,
  addedOptions: 0,
  show: true,
  showBalanceDue: true
} as RenderableTotals

export default function TotalTableNext() {
  const classes = useStyles()
  const { quote, isEditable, isPresentation, isCustomerView, onViewCalculations } = useQuote()
  const { options } = useClientOptions()
  // Promo line + compact application row share one bordered block at the foot of the table.
  // Either one alone is enough to draw it.
  const { showPromo, showApplication } = useWisetackSection()
  const showWisetack = showPromo || showApplication

  // `getTotals` returns `{}` for a document worth nothing (see get-totals.ts), which used to remove
  // the summary card entirely. The editor now keeps it and reads $0.00, so a new estimate shows where
  // its total will land instead of a gap (ENG-1478). The customer-facing document still drops it —
  // a $0.00 total is not something to send anyone.
  const rawTotals = getTotals({ quote, consumer: 'customer', options }) as RenderableTotals
  const hasTotals = !!rawTotals && Object.keys(rawTotals).length > 0
  if (!hasTotals && !isEditable) {
    return null
  }
  const totals = hasTotals ? rawTotals : ZERO_TOTALS

  const objectLabels = getObjectLabels({ options, invoice: quote.is_invoice })
  const { allowQuoteAdditionalWork, splitSubtotal, surcharge } = getQuoteOptions({ quote, options })
  const details = getDetails({ quote, options })

  const hasAdditionalWork = quote.is_invoice || allowQuoteAdditionalWork
  const showSurcharge = !!totals.serviceFees?.surcharge
  const showAmountPaid = !!totals.amountPaid || quote.is_invoice
  const { subTotal, customSubTotal, afterTax, discounts = [], taxes = [], tierAdjustment } = totals

  const showPackage = !!quote.tieredPackages?.enabled
  const rows: BreakdownRow[] = []

  if (tierAdjustment?.amount && !showPackage) {
    rows.push({
      key: 'tier',
      label: tierAdjustment.level ? `${tierAdjustment.label} (${tierAdjustment.level})` : tierAdjustment.label,
      value: tierAdjustment.amount,
      prefixPlus: tierAdjustment.amount > 0
    })
  }
  if (totals.addedOptions) {
    rows.push({
      key: 'added-options',
      label: `${objectLabels.option.plural} ${quote.status.value === 'accepted' || quote.is_invoice ? 'Accepted' : 'Added'}`,
      value: totals.addedOptions
    })
  }
  if (hasAdditionalWork && totals.additionalWork) {
    rows.push({ key: 'additional-work', label: objectLabels.additionalWork.value, value: totals.additionalWork })
  }

  discounts.forEach((discount) => {
    if (!discount.amount) return
    const fallback = discount.negative ? 'Additional: ' : 'Discount: '
    rows.push({ key: discount.key, label: discount.description || fallback, value: discount.amount })
  })

  taxes.forEach((tax, index) => {
    rows.push({ key: `tax-${index}`, label: tax.description, value: tax.amount })
  })

  if (showSurcharge) {
    rows.push({
      key: 'surcharge',
      label: `Credit Card Surcharge (${surcharge?.value}%)`,
      value: Math.round((totals.serviceFees?.surcharge ?? 0) * 100) / 100
    })
  }

  const renderRow = (key: string, label: string, value: number, prefixPlus?: boolean) => (
    <div key={key} className={classes.row}>
      <span>{label}</span>
      <span>
        {prefixPlus && '+'}
        <Typography component="span" variant={'body1'}>
          {formatCurrency({ options, value })}
        </Typography>
      </span>
    </div>
  )

  // The subtotal rows: labor/materials when the subtotal is split, otherwise a single "Sub total" row
  // (only when it differs from the grand total). Collected up front so the bordered wrapper is only
  // rendered when there's something to show — an empty box would otherwise appear when the split
  // components are both zero, or when subTotal === afterTax (no adjustments).
  const subtotalRows =
    splitSubtotal && !customSubTotal
      ? [
          !!details.totals.labour && renderRow('labor', 'Labor', details.totals.labour),
          !!details.totals.materials && renderRow('materials', 'Materials', details.totals.materials)
        ].filter(Boolean)
      : subTotal && subTotal !== afterTax
        ? [renderRow('subtotal', 'Sub total', subTotal)]
        : []

  // The breakdown block (subtotal → adjustment line items → grand-total → amount paid / balance due).
  // Shared by the non-package and tiered-package layouts; each passes the rows it wants shown.
  const renderBreakdown = (breakdownRows: BreakdownRow[]) => {
    // Nothing above the grand total and nothing below it — the row is the whole card (ENG-1917).
    const totalIsSolo =
      subtotalRows.length === 0 &&
      breakdownRows.length === 0 &&
      !showPackage &&
      !showAmountPaid &&
      !showSurcharge &&
      !showWisetack

    return (
      <div className={classNames({ [classes.breakdown]: true, [classes.breakdownSolo]: totalIsSolo })}>
        {subtotalRows.length > 0 && (
          <div className={classNames({ [classes.headerRow]: true, [classes.noBottomBorder]: showPackage })}>
            {subtotalRows}
          </div>
        )}
        {showPackage && (
          <div className={classes.packageBlock} onClick={(e) => e.stopPropagation()} role="presentation">
            <PackageSelectedRow />
          </div>
        )}
        {breakdownRows.length > 0 && (
          <div className={classes.breakdownRows}>
            {breakdownRows.map((row) => renderRow(row.key, row.label, row.value, row.prefixPlus))}
          </div>
        )}
        <div
          className={classNames({
            [classes.row]: true,
            [classes.headerRow]: true,
            [classes.totalRow]: true,
            [classes.totalRowSolo]: totalIsSolo,
            [classes.noBottomBorder]: showAmountPaid || showSurcharge || showWisetack
          })}
        >
          <span>Total</span>
          <Typography component="span" value={afterTax} format={'price'} showUnits showZeros variant={'body1'} />
        </div>

        {showAmountPaid && (
          <div
            className={classNames({
              [classes.paymentSection]: true,
              [classes.paymentSectionJoined]: showWisetack
            })}
          >
            {showAmountPaid && (
              <div className={classes.paymentRow}>
                <span>Amount Paid</span>
                <Typography
                  component="span"
                  value={totals.amountPaid || 0}
                  format={'price'}
                  showUnits
                  showZeros
                  variant={'body1'}
                />
              </div>
            )}
            {totals.showBalanceDue && (showAmountPaid || showSurcharge) && (
              <div className={classes.paymentRow}>
                <span>Balance Due</span>
                <Typography
                  component="span"
                  value={totals.balanceDue || 0}
                  format={'price'}
                  showUnits
                  showZeros
                  variant={'body1'}
                />
              </div>
            )}
          </div>
        )}
        {showWisetack && (
          <div className={classes.wisetackPromoWrapper} onClick={(e) => e.stopPropagation()} role="presentation">
            {showPromo && (
              <WisetackPromo inPresentation={!!isPresentation} inCustomerView={!!isCustomerView} showWhenDeclined />
            )}
            <WisetackApplicationNext />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <OutlinedTableCard className={classes.card} singleBorder hideHeader>
        {onViewCalculations ? (
          <div
            className={classes.clickable}
            role="button"
            tabIndex={0}
            onClick={() => onViewCalculations()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onViewCalculations()
              }
            }}
          >
            {renderBreakdown(rows)}
          </div>
        ) : (
          <div>{renderBreakdown(rows)}</div>
        )}
      </OutlinedTableCard>
    </div>
  )
}
